import React from 'react';
import { Box, CircularProgress, useTheme } from '@mui/material';

export interface PreviewCanvasProps {
  children: React.ReactNode;
  compact?: boolean;
  loading?: boolean;
  checkerboard?: boolean;
  role?: string;
  'aria-label'?: string;
}

export const PreviewCanvas: React.FC<PreviewCanvasProps> = ({
  children,
  compact = false,
  loading = false,
  checkerboard = false,
  role = 'region',
  'aria-label': ariaLabel = 'File preview',
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const minHeight = compact ? 200 : 'calc(80vh - 48px)';

  const checkerboardBg = checkerboard
    ? {
        backgroundColor: isDark ? '#2a2a2a' : '#e8e8e8',
        backgroundImage: isDark
          ? 'linear-gradient(45deg, #333 25%, transparent 25%), linear-gradient(-45deg, #333 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #333 75%), linear-gradient(-45deg, transparent 75%, #333 75%)'
          : 'linear-gradient(45deg, #ccc 25%, transparent 25%), linear-gradient(-45deg, #ccc 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #ccc 75%), linear-gradient(-45deg, transparent 75%, #ccc 75%)',
        backgroundSize: '16px 16px',
        backgroundPosition: '0 0, 0 8px, 8px -8px, -8px 0',
      }
    : {
        bgcolor: isDark ? 'grey.900' : 'grey.100',
      };

  return (
    <Box
      role={role}
      aria-label={ariaLabel}
      aria-busy={loading}
      sx={{
        position: 'relative',
        width: '100%',
        minHeight,
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'auto',
        ...checkerboardBg,
      }}
    >
      {children}
      {loading && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: isDark ? 'rgba(0,0,0,0.4)' : 'rgba(255,255,255,0.6)',
            zIndex: 2,
          }}
        >
          <CircularProgress aria-hidden />
        </Box>
      )}
    </Box>
  );
};

export default PreviewCanvas;
